/**
 * Utilitaires de formatage pour l'application PrestaSlot
 */
import { formatDateInTimeZone, formatTimeInTimeZone } from './timezone';
import { fr } from 'date-fns/locale';

/**
 * Formate un montant en devise
 * @param amount - Montant à formater
 * @param currency - Code de la devise (par défaut: EUR)
 * @param locale - Locale pour le formatage (par défaut: fr-FR)
 * @returns Chaîne formatée
 */
export function formatCurrency(amount: number | undefined, currency: string = 'EUR', locale: string = 'fr-FR'): string| undefined {
  if(!amount) return
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
}

/**
 * Formate une durée en minutes en format lisible
 * @param minutes - Durée en minutes
 * @returns Chaîne formatée (ex: "1h30min", "45min", "2h")
 */
export function formatDuration(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  
  if (hours > 0 && mins > 0) {
    return `${hours}h${mins}min`;
  } else if (hours > 0) {
    return `${hours}h`;
  } else {
    return `${mins}min`;
  }
}

/**
 * Formate une date en chaîne lisible
 * @param date - Date à formater ou chaîne ISO
 * @param locale - Locale pour le formatage (par défaut: fr-FR)
 * @returns Chaîne formatée
 */
export function formatDate(date: Date | string, locale: string = 'fr-FR'): string {
  // Utilise l'utilitaire de formatage avec le fuseau horaire du professionnel
  return formatDateInTimeZone(date, 'dd/MM/yyyy', { locale: locale === 'fr-FR' ? fr : undefined });
}

/**
 * Formate une heure en chaîne lisible
 * @param date - Date contenant l'heure à formater ou chaîne ISO
 * @param locale - Locale pour le formatage (par défaut: fr-FR)
 * @returns Chaîne formatée
 */
export function formatTime(date: Date | string, locale: string = 'fr-FR'): string {
  // Utilise l'utilitaire de formatage avec le fuseau horaire du professionnel
  return formatTimeInTimeZone(date, 'HH:mm', { locale: locale === 'fr-FR' ? fr : undefined });
}

/**
 * Slugifie une chaîne de caractères pour l'utiliser dans une URL
 * @param text - Texte à slugifier
 * @returns Chaîne slugifiée (minuscules, sans accents, avec tirets)
 */
export function slugify(text: string | undefined | null): string {
  if (!text) return '';

  return text
    .toString()
    .toLowerCase()
    .normalize('NFD') // Décompose les caractères accentués
    .replace(/[\u0300-\u036f]/g, '') // Supprime les accents
    .replace(/[^a-z0-9\s-]/g, '') // Garde uniquement lettres, chiffres, espaces et tirets
    .trim()
    .replace(/\s+/g, '-') // Remplace les espaces par des tirets
    .replace(/-+/g, '-'); // Évite les tirets multiples consécutifs
}
